import {
  AdvisorDataService,
  FaceProfileData
} from '@ampcore/client-side-services'
import Cookies from 'js-cookie'
import { useEffect, useState } from 'react'

import { useAmpUserStatus } from '../../hooks/useAmpUserStatus'
import { Link } from '../../types/Link.type'
import { UserStatus } from '../../types/UserStatus.enum'
import type {
  AdvisorQuestionContent,
  AdvisorQuestionProps
} from './AdvisorQuestion.type'
import { generateMailtoLink } from './generateMailtoLink'

export type UseResolvedDynamicContentReturn = {
  dynamicContent: AdvisorQuestionContent | null
  advisorData: FaceProfileData | undefined
  isLoading: boolean
}

export function useResolvedDynamicContent(
  props: AdvisorQuestionProps
): UseResolvedDynamicContentReturn {
  const [dynamicContent, setDynamicContent] =
    useState<AdvisorQuestionContent | null>(null)
  const [advisorData, setAdvisorData] = useState<FaceProfileData | undefined>()
  const [isLoading, setIsLoading] = useState(false)
  const userStatus = useAmpUserStatus()

  useEffect(() => {
    const generateContent = async () => {
      const advisorDataService = new AdvisorDataService()
      const clientContent = props.client ?? props.prospect
      const prospectContent = props.prospect

      // fallback to prospect content if client content is not available for legacy support
      const emailSubject =
        props.client?.emailSubject ??
        props.prospect?.emailSubject ??
        props.emailSubject
      const emailBody =
        props.client?.emailBody ?? props.prospect?.emailBody ?? props.emailBody
      const faceProfileCookie = Cookies.get('FaceProfileCookie')

      if (
        faceProfileCookie &&
        clientContent &&
        (userStatus == UserStatus.CLIENT_ACTIVE ||
          userStatus == UserStatus.CLIENT_EXPIRED)
      ) {
        setIsLoading(true)
        const faceProfileResponse =
          await advisorDataService.getById(faceProfileCookie)
        setIsLoading(false)

        if (
          faceProfileResponse.errors &&
          faceProfileResponse.errors.length > 0
        ) {
          setDynamicContent({
            bgColor: clientContent.bgColor,
            iconColor: clientContent.iconColor,
            headline: clientContent.headline,
            body: props.loadErrorText.replace(/%page%/g, window.location.href),
            questions: Array.isArray(clientContent.questions)
              ? clientContent.questions
              : [clientContent.questions]
          })
          return
        }

        if (
          faceProfileResponse?.data?.name &&
          (faceProfileResponse.data.phone || faceProfileResponse.data.email)
        ) {
          let clientContentBody: string
          const name = faceProfileResponse.data.name ?? 'an advisor'
          const advisorNameLink = faceProfileResponse.data.urls?.site
          if (advisorNameLink) {
            clientContentBody = clientContent.resultsBody.replace(
              /%advisor%/g,
              `<a data-analytics="${name}" href="${advisorNameLink}">${name}</a>`
            )
          } else {
            clientContentBody = clientContent.resultsBody.replace(
              /%advisor%/g,
              name
            )
          }

          setAdvisorData(faceProfileResponse.data)
          let button: Link | undefined
          if (faceProfileResponse.data.email) {
            button = {
              name: clientContent.buttonText ?? 'Email Advisor',
              url: generateMailtoLink(
                faceProfileResponse.data.email,
                emailBody,
                emailSubject
              )
            }
          } else if (faceProfileResponse.data.phone) {
            button = {
              name: props.buttonTextPhone ?? 'Call Advisor',
              url: `tel:${faceProfileResponse.data.phone}`
            }
          } else {
            console.error('No contact information available for advisor.')
          }
          if (button) {
            setDynamicContent({
              bgColor: clientContent.bgColor,
              iconColor: clientContent.iconColor,
              headline: clientContent.headline,
              body: clientContentBody,
              emailSubject: emailSubject,
              emailBody: emailBody,
              questions: Array.isArray(clientContent.questions)
                ? clientContent.questions
                : [clientContent.questions],
              button
            })
            return
          }
        }
      }

      if (!prospectContent) {
        setDynamicContent(null)
        return
      }

      if ((prospectContent.resultsBody ?? '').includes('%advisor%')) {
        setDynamicContent({
          bgColor: clientContent.bgColor,
          iconColor: clientContent.iconColor,
          headline: clientContent.headline,
          body: props.loadErrorText.replace(/%page%/g, window.location.href),
          emailBody: clientContent.emailBody,
          questions: Array.isArray(clientContent.questions)
            ? clientContent.questions
            : [clientContent.questions]
        })
        return
      }

      setDynamicContent({
        bgColor: prospectContent.bgColor,
        iconColor: prospectContent.iconColor,
        body: prospectContent.resultsBody,
        headline: prospectContent.headline,
        questions: Array.isArray(prospectContent.questions)
          ? prospectContent.questions
          : [prospectContent.questions],
        emailBody: prospectContent.emailBody,
        button: {
          name: prospectContent.buttonText ?? 'Find an Advisor',
          url: props.buttonHrefProspect!,
          target: '_blank'
        }
      })
    }

    generateContent()
  }, [userStatus, props])

  return { dynamicContent, advisorData, isLoading }
}
